import { makeSearchParam } from '@/components/people/people/utils';
import { useDebounce } from '@/hooks/debounce';
import type { LoadPeopleSearchParams as PeopleSearchParams } from '@/store/people/actions/people';
import { usePeopleLookup } from '@/store/people/hooks';
import { Autocomplete, AutocompleteItem, Input } from '@/components/ui';
import type { ChangeEvent, Key, ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { PersonItem } from '../output/person';
import { getFullName } from '@/shared/lib/names';
import type { Person } from '@/store/people/types';

export function PeopleSearch({
  label,
  placeholder,
  startContent,
  onChange,
  className,
}: {
  label?: string;
  placeholder?: string;
  startContent?: ReactNode;
  onChange?: (_value: PeopleSearchParams | undefined) => void;
  className?: string;
}) {
  const last = useRef<PeopleSearchParams | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const searchValue = useDebounce(searchInput, 400);
  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    const search = makeSearchParam(searchValue);
    if (!search && !last.current) {
      return;
    }

    if (
      search &&
      last.current &&
      search.type === last.current?.type &&
      search.value === last.current?.value
    ) {
      return;
    }

    last.current = search;
    onChange?.(search);
  }, [searchValue, onChange]);

  return (
    <Input
      className={className}
      label={label}
      aria-label='Search for Person'
      placeholder={placeholder}
      startContent={startContent}
      onChange={onSearchChange}
      isClearable
      onClear={() => setSearchInput('')}
    />
  );
}

export function PeopleAutocomplete({
  label,
  className,
  onChange,
  email,
  phone,
  itemContent,
}: {
  label?: string;
  className?: string;
  onChange?: (_item: Person | null) => void;
  email?: boolean;
  phone?: boolean;
  itemContent?: ({ item }: { item: Person }) => ReactElement;
}) {
  const [search, setSearch] = useState('');
  const { people, loading } = usePeopleLookup({ search });
  const person = useRef<Person | null>(null);
  const onSelectionChange = (key: Key | null) => {
    const id = key ? (key as string) : null;
    let current: Person | null = null;
    if (id) {
      current = people.find((item) => item.id === id) || null;
    }

    if (current !== person.current) {
      person.current = current;
      onChange?.(current);
    }
  };

  return (
    <Autocomplete
      label={label}
      items={people}
      className={className}
      isLoading={loading}
      inputValue={search}
      placeholder='Search by name, email or phone...'
      listboxProps={{
        emptyContent: !search ? 'Type to start searching' : 'No results found',
      }}
      onInputChange={setSearch}
      onSelectionChange={onSelectionChange}
    >
      {(item) => {
        return (
          <AutocompleteItem key={item.id} textValue={getFullName(item.data)}>
            {itemContent ? (
              itemContent({ item })
            ) : (
              <PersonItem item={item} phone={phone} email={email} badge />
            )}
          </AutocompleteItem>
        );
      }}
    </Autocomplete>
  );
}
